import React, { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { cn } from '@/lib/utils';
import CONFIG from '@/lib/config';
import { 
  Activity,
  LayoutDashboard, 
  Users, 
  Building2, 
  BarChart3, 
  FileText, 
  Settings, 
  Mail, 
  LogOut,
  Menu,
  X,
  ChevronDown,
  PanelLeftClose,
  PanelLeftOpen,
  UserCheck
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import HubSwitcher from './HubSwitcher';

interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

interface AdminLayoutProps {
  children: React.ReactNode;
}

const sidebarItems = [
  {
    title: 'Dashboard',
    icon: LayoutDashboard,
    href: '/admin',
    description: 'Overview and statistics'
  },
  {
    title: 'Hubs',
    icon: Building2,
    href: '/admin/hubs',
    description: 'Manage hub subscriptions'
  },
  {
    title: 'Projects',
    icon: FileText,
    href: '/admin/projects',
    description: 'View all projects'
  },
  {
    title: 'Data Syncs',
    icon: Activity,
    href: '/admin/data-syncs',
    description: 'Monitor ACC API sync status'
  },
  {
    title: 'Users',
    icon: Users,
    href: '/admin/users',
    description: 'View all application users'
  },
  {
    title: 'ACC Users',
    icon: UserCheck,
    href: '/admin/acc-users',
    description: 'View ACC users from Autodesk'
  },
  {
    title: 'Analytics',
    icon: BarChart3,
    href: '/admin/analytics',
    description: 'Data analysis and insights'
  },
  {
    title: 'Audit Logs',
    icon: FileText,
    href: '/admin/audit-logs',
    description: 'System activity logs'
  },
  {
    title: 'Reports',
    icon: FileText,
    href: '/admin/reports',
    description: 'Generate and download reports'
  },
  {
    title: 'Emails',
    icon: Mail,
    href: '/admin/notifications',
    description: 'Manage email templates and send emails'
  },
  {
    title: 'Access Requests',
    icon: UserCheck,
    href: '/admin/access-requests',
    description: 'Manage access requests and free trial requests'
  },
  {
    title: 'Settings',
    icon: Settings,
    href: '/admin/settings',
    description: 'System configuration'
  }
];

const AdminLayout = React.memo(function AdminLayout({ children }: AdminLayoutProps) {
  const [location, setLocation] = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedHubId, setSelectedHubId] = useState<string | null>(null);

  useEffect(() => {
    // Check admin authentication via session
    const checkAdminAuth = async () => {
      try {
        console.log('🔍 AdminLayout: Checking admin authentication...');
        
        // Check session via API
        const response = await fetch('/api/admin/user', {
          credentials: 'include' // Important for session cookies
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.user) {
            console.log('✅ AdminLayout: Session found, authentication successful');
            setAdminUser(data.user);
            setIsLoading(false);
          } else {
            console.log('❌ AdminLayout: No valid session, redirecting to login');
            setLocation('/admin/login');
          }
        } else {
          console.log('❌ AdminLayout: Session check failed, redirecting to login');
          setLocation('/admin/login');
        }
      } catch (error) {
        console.error('❌ AdminLayout: Error checking session:', error);
        setLocation('/admin/login');
      }
    };

    checkAdminAuth();
  }, [setLocation]);

  const handleLogout = async () => {
    try {
      console.log('🔓 AdminLayout: Logging out...');
      
      // Call logout API to destroy session
      await fetch('/api/admin/logout', {
        method: 'POST',
        credentials: 'include'
      });
      
      console.log('✅ AdminLayout: Session destroyed, redirecting to login');
      setLocation('/admin/login');
    } catch (error) {
      console.error('❌ AdminLayout: Logout error:', error);
      // Still redirect even if logout API fails
      setLocation('/admin/login');
    }
  };

  const toggleSidebar = () => {
    setSidebarExpanded(!sidebarExpanded);
  };

  const isActiveRoute = (href: string) => {
    if (href === '/admin') {
      // For dashboard, only match exact path
      return location === '/admin';
    }
    // For other routes, match exact path or sub-paths
    return location === href || location.startsWith(href + '/');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col lg:flex-row">
      {/* Mobile sidebar overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Fixed Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 bg-red-900 transform transition-all duration-300 ease-in-out lg:translate-x-0
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        ${sidebarExpanded ? 'w-64' : 'w-16'}
      `}>
        <div className="flex items-center justify-between h-16 px-4 lg:px-6 border-b border-red-800">
          <div className={cn(
            "flex items-center transition-all duration-300",
            sidebarExpanded ? "justify-start" : "justify-center w-full"
          )}>
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center mr-2 lg:mr-3 flex-shrink-0">
              <span className="text-red-600 font-bold text-sm">A</span>
            </div>
            <h1 className={cn(
              "text-white font-semibold text-base lg:text-lg truncate transition-opacity duration-300",
              sidebarExpanded ? "opacity-100" : "opacity-0 lg:hidden"
            )}>Admin Console</h1>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden text-white hover:bg-red-800 flex-shrink-0"
            onClick={() => setIsSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        <nav className={cn(
          "mt-6 overflow-y-auto h-[calc(100vh-8rem)] scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-transparent hover:scrollbar-thumb-gray-400",
          sidebarExpanded ? "px-3 lg:px-4" : "px-2"
        )}>
          <ul className="space-y-1">
            {sidebarItems.map((item) => {
              const Icon = item.icon;
              const isActive = isActiveRoute(item.href);
              
              return (
                <li key={item.href}>
                  <div
                    className={cn(
                      "flex items-center px-6 py-3 hover:bg-gray-700 hover:text-white cursor-pointer transition-all duration-300",
                      isActive ? "text-white bg-gray-600" : "text-gray-300",
                      !sidebarExpanded && "lg:justify-center lg:px-3"
                    )}
                    title={!sidebarExpanded ? item.title : undefined}
                    onClick={() => {
                      setLocation(item.href);
                      setIsSidebarOpen(false);
                    }}
                  >
                    <Icon className={cn(
                      "h-5 w-5",
                      sidebarExpanded ? "mr-3" : "mr-0"
                    )} />
                    <span className={cn(
                      "transition-opacity duration-300",
                      sidebarExpanded ? "opacity-100" : "opacity-0 lg:hidden"
                    )}>
                      {item.title}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Toggle Sidebar Button */}
        <div className="absolute bottom-4 left-0 right-0 px-2">
          <button
            onClick={toggleSidebar}
            className={cn(
              "flex items-center justify-center text-white rounded-md transition-all duration-200 shadow-lg",
              sidebarExpanded 
                ? "w-full px-4 py-2 bg-red-700 hover:bg-red-600 border-2 border-red-500" 
                : "w-full lg:w-10 lg:h-10 lg:rounded-full bg-red-700 hover:bg-red-600 border-2 border-red-500 lg:shadow-xl"
            )}
            title={sidebarExpanded ? "Collapse Sidebar" : "Expand Sidebar"}
          >
            {sidebarExpanded ? (
              <>
                <PanelLeftClose className="h-5 w-5 mr-2" />
                <span className="text-sm font-medium">Collapse</span>
              </>
            ) : (
              <>
                <div className="flex items-center justify-center w-full h-full">
                  <PanelLeftOpen className="h-5 w-5 text-white" />
                </div>
                <span className="text-sm lg:hidden font-medium">Expand</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Main content with fixed header */}
      <div className={cn(
        "flex-1 flex flex-col min-w-0 transition-all duration-300",
        sidebarExpanded ? "lg:ml-64" : "lg:ml-16"
      )}>
        {/* Fixed Top bar */}
        <header className={cn(
          "fixed top-0 right-0 bg-white shadow-sm border-b border-gray-200 flex-shrink-0 z-30 transition-all duration-300",
          sidebarExpanded ? "lg:left-64" : "lg:left-16"
        )}>
          <div className="flex items-center justify-between h-16 px-4 lg:px-6">
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden"
              onClick={() => setIsSidebarOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </Button>
            
            <div className="flex-1" />
            
            <div className="flex items-center space-x-2 lg:space-x-4">
              
              {/* User avatar moved to top right */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center space-x-2 text-gray-700 hover:bg-gray-100"
                  >
                    <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-medium text-sm">
                        {adminUser?.firstName?.charAt(0)}{adminUser?.lastName?.charAt(0)}
                      </span>
                    </div>
                    <div className="hidden sm:block text-left">
                      <div className="text-sm font-medium">
                        {adminUser?.firstName} {adminUser?.lastName}
                      </div>
                      <div className="text-xs text-gray-500">{adminUser?.email}</div>
                    </div>
                    <ChevronDown className="w-4 h-4 ml-1 flex-shrink-0" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content with top margin for fixed header */}
        <main className="flex-1 p-4 lg:p-6 overflow-auto mt-16">
          <div className="max-w-full">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
});

export default AdminLayout;
